/**
 * uploads.service.ts
 *
 * Size-aware file upload helpers. For files under 4 MB, uploads use a direct
 * multipart POST (existing behavior). For larger files, the browser obtains an
 * S3 presigned PUT URL to bypass Lambda's 6 MB payload limit, uploads directly
 * to S3, then notifies the backend with the s3Key. The backend AcceptsS3Upload
 * trait resolves either form transparently.
 */
import { apiClient } from './apiClient';
import type { AxiosResponse } from 'axios';

/**
 * Files at or below this size go multipart; larger files switch to the
 * presigned-S3 flow to stay under Lambda's 6 MB payload limit.
 */
export const SIZE_THRESHOLD = 4 * 1024 * 1024; // 4 MB

/**
 * Ceiling for the *combined* size of everything left on the multipart path in a
 * single request. The per-file threshold above is not enough on its own: three
 * 3.9 MB files each clear it and still add up to ~11.7 MB in one body. Lambda's
 * hard cap is 6 MB and the body is base64-inflated (~1.34x) before it reaches
 * the function, leaving ~4.5 MB of usable raw payload; this stays under that so
 * the form fields and multipart boundaries still fit.
 */
export const INLINE_BUDGET = Math.floor(3.5 * 1024 * 1024); // 3.5 MB

/** How long a direct-to-S3 PUT may run before we give up on it. */
const S3_PUT_TIMEOUT_MS = 120_000;

interface PresignResponse {
  uploadUrl: string;
  s3Key: string;
}

/** Upload progress: percent, and bytes sent of the total. */
export type UploadProgressCallback = (percent: number, loaded: number, total: number) => void;

interface PresignOptions {
  /**
   * Tells /uploads/presign what the file is for. `video_library` lets a video
   * run to the library's 500 MB ceiling; everything else is capped at 100 MB.
   * `patient-files` makes the API refuse a file type a patient's files do not
   * take before any URL is signed, so nothing is sent to S3.
   */
  purpose?: 'video_library' | 'patient-files';
  /** Overrides file.type, which browsers often leave empty for .mov. */
  contentType?: string;
  /** Reports bytes sent on the S3 PUT. */
  onProgress?: UploadProgressCallback;
}

interface UploadFileOptions {
  /** Multipart field name used for the small-file path. Defaults to `file`. */
  fileFieldName?: string;
  /** Progress callback, fired on both the multipart and the presigned path. */
  onProgress?: UploadProgressCallback;
  /** Passed through to the presign request on the large-file path. */
  presign?: Omit<PresignOptions, 'onProgress'>;
}

/**
 * With a progress callback the PUT goes over XMLHttpRequest, since fetch has no
 * upload-progress event. A large file can legitimately take many minutes, so
 * instead of a fixed timeout it gives up only once no bytes have moved for this
 * long.
 */
const S3_PUT_STALL_MS = 60_000;

/**
 * Presign + PUT a single file directly to S3, returning its s3Key. Callers use
 * this when they need to attach the key to an existing request body themselves.
 */
export async function presignAndPutFile(file: File, opts: PresignOptions = {}): Promise<string> {
  const contentType = opts.contentType || file.type || 'application/octet-stream';
  const response = await apiClient.post('/uploads/presign', {
    filename: file.name,
    contentType,
    size: file.size,
    ...(opts.purpose ? { purpose: opts.purpose } : {}),
  });

  // apiClient's response interceptor wraps any plain payload that has no `data`
  // key, and /uploads/presign answers with a bare { uploadUrl, s3Key } — so the
  // fields arrive one level down. Read both shapes, exactly as the Zernio media
  // path does.
  //
  // The guard is not defensive padding. fetch(undefined) resolves "undefined"
  // against the app origin and CloudFront answers index.html with a 200, so an
  // unchecked read uploads nothing, passes the putRes.ok check below, and then
  // posts the string "undefined" as the s3Key — which the API rejects with
  // "Invalid s3Key prefix." from AcceptsS3Upload, pointing at the wrong layer.
  const presigned = (response.data?.data ?? response.data ?? {}) as Partial<PresignResponse>;
  if (!presigned.uploadUrl || !presigned.s3Key) {
    console.error('[uploads] presign returned no upload URL', response.data);
    throw new Error(`Could not start the upload for ${file.name}.`);
  }

  if (opts.onProgress) {
    await putWithProgress(presigned.uploadUrl, file, contentType, opts.onProgress);
    return presigned.s3Key;
  }

  // This PUT goes straight to S3, so it bypasses apiClient entirely — no
  // interceptor, no timeout, no error translation. Left bare, a CORS or network
  // failure rejects with an unhelpful `TypeError: Failed to fetch` and a stalled
  // connection hangs forever, which surfaces as "the send just does nothing".
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), S3_PUT_TIMEOUT_MS);

  let putRes: Response;
  try {
    putRes = await fetch(presigned.uploadUrl, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': contentType },
      signal: controller.signal,
    });
  } catch {
    if (controller.signal.aborted) {
      throw new Error(`Timed out uploading ${file.name} to file storage.`);
    }
    throw new Error(`Could not upload ${file.name} to file storage.`);
  } finally {
    clearTimeout(timer);
  }

  if (!putRes.ok) {
    throw new Error(`Could not upload ${file.name} to file storage (${putRes.status}).`);
  }

  return presigned.s3Key;
}

/** The presigned PUT over XMLHttpRequest, reporting bytes sent as it goes. */
function putWithProgress(
  url: string,
  file: File,
  contentType: string,
  onProgress: UploadProgressCallback,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    let stalled = false;
    let stallTimer = setTimeout(() => { stalled = true; xhr.abort(); }, S3_PUT_STALL_MS);
    const resetStall = () => {
      clearTimeout(stallTimer);
      stallTimer = setTimeout(() => { stalled = true; xhr.abort(); }, S3_PUT_STALL_MS);
    };

    xhr.open('PUT', url);
    // Must match the type the server signed, or S3 answers SignatureDoesNotMatch.
    xhr.setRequestHeader('Content-Type', contentType);
    xhr.upload.onprogress = (e) => {
      resetStall();
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100), e.loaded, e.total);
    };
    xhr.onload = () => {
      clearTimeout(stallTimer);
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`Could not upload ${file.name} to file storage (${xhr.status}).`));
    };
    xhr.onerror = () => {
      clearTimeout(stallTimer);
      reject(new Error(`Could not upload ${file.name} to file storage.`));
    };
    xhr.onabort = () => {
      clearTimeout(stallTimer);
      reject(new Error(stalled ? `Timed out uploading ${file.name} to file storage.` : 'Upload cancelled.'));
    };
    xhr.send(file);
  });
}

/**
 * Upload a file to the given API endpoint, automatically choosing the
 * presigned S3 path for files over 4 MB.
 *
 * @param endpoint - The API path (e.g. `/kb/uploads`)
 * @param file - The File object to upload
 * @param extraFields - Additional form fields to include in the request
 * @param options - Optional multipart field name and progress callback
 * @returns The axios response from the final backend POST
 */
export async function uploadFile(
  endpoint: string,
  file: File,
  extraFields?: Record<string, string>,
  options?: UploadFileOptions,
): Promise<AxiosResponse> {
  const appendExtras = (form: FormData) => {
    if (extraFields) {
      for (const [k, v] of Object.entries(extraFields)) {
        form.append(k, v);
      }
    }
  };

  if (file.size <= SIZE_THRESHOLD) {
    const form = new FormData();
    form.append(options?.fileFieldName ?? 'file', file);
    appendExtras(form);
    return apiClient.post(endpoint, form, {
      onUploadProgress: options?.onProgress
        ? (e) => {
            if (e.total) options.onProgress!(Math.round((e.loaded / e.total) * 100), e.loaded, e.total);
          }
        : undefined,
    });
  }

  const s3Key = await presignAndPutFile(file, { ...options?.presign, onProgress: options?.onProgress });

  const form = new FormData();
  form.append('s3Key', s3Key);
  appendExtras(form);
  return apiClient.post(endpoint, form);
}

/**
 * Split a set of files by size: small files are returned as-is to be sent
 * multipart (e.g. `attachments[]`); the rest are presigned + PUT to S3 and
 * returned as s3Keys to be sent alongside (e.g. `s3Keys[]`). The backend
 * resolveUploadedFiles() trait method merges both into one list.
 *
 * A file goes multipart only if it clears BOTH gates: it is individually under
 * SIZE_THRESHOLD, and it still fits in the request's remaining INLINE_BUDGET.
 * The second gate is what keeps several individually-small files from summing
 * past Lambda's payload cap. Files are walked in the caller's order so the
 * attachment order is not reshuffled.
 */
export async function presignLargeFiles(
  files: File[],
): Promise<{ multipartFiles: File[]; s3Keys: string[] }> {
  const multipartFiles: File[] = [];
  const large: File[] = [];
  let inlineTotal = 0;

  for (const file of files) {
    if (file.size <= SIZE_THRESHOLD && inlineTotal + file.size <= INLINE_BUDGET) {
      multipartFiles.push(file);
      inlineTotal += file.size;
    } else {
      large.push(file);
    }
  }

  const s3Keys = await Promise.all(large.map((f) => presignAndPutFile(f)));

  return { multipartFiles, s3Keys };
}
